import React, { useEffect, useState } from 'react';
import { adminAPI } from '../api';

/**
 * Admin → System Health.
 *
 * A single compact card with five quiet operational signals. Editorial
 * aesthetic: no charts, no metrics, no alarms. The dot is the only colour
 * cue — everything else is calm taupe / ivory / charcoal.
 *
 * Status colours are intentionally soft:
 *   ok        → muted sage (#7d9276)
 *   attention → muted amber (#b88a4a)
 */

const DOT = {
  ok:        '#7d9276',
  attention: '#b88a4a',
};

const SystemHealthCard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    adminAPI.getSystemHealth()
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []);

  if (error || !data) return null;

  const signals = data.signals || [];

  return (
    <section
      data-testid="admin-system-health"
      style={{
        marginTop: 56,
        maxWidth: 520,
        padding: '22px 26px 24px',
        border: '1px solid var(--kahla-divider, #e8e3da)',
        borderRadius: 2,
        background: 'transparent',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            fontWeight: 500,
          }}
        >
          System Health
        </h3>
        <span
          data-testid="admin-system-health-overall"
          style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: 13,
            color: 'var(--kahla-text-light)',
            opacity: 0.85,
          }}
        >
          {data.overall === 'ok' ? 'All quiet' : 'Needs a look'}
        </span>
      </header>

      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
        }}
      >
        {signals.map((s) => (
          <li
            key={s.key}
            data-testid={`admin-system-health-${s.key}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '9px 0',
              borderBottom: '1px solid var(--kahla-divider-soft, #f0ebe1)',
              gap: 16,
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 13,
                color: 'var(--kahla-charcoal, #2a2a2a)',
                letterSpacing: '0.01em',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: DOT[s.status] || DOT.attention,
                  flexShrink: 0,
                }}
              />
              {s.label}
            </span>
            <span
              style={{
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic',
                fontSize: 13,
                color: 'var(--kahla-text-light)',
                textAlign: 'right',
              }}
            >
              {s.detail}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default SystemHealthCard;
